<template>
  <fullscreen-dialog
    v-model="model"
    :title="user ? toLatin(user.user_name) : ''"
    :subtitle="`ID: ${user ? user.user_id : ''}`"
    hide-footer
  >
    <template #default>
      <v-card-text>
        <div v-if="!user">{{ $t("no_data") }}</div>
        <div v-else>
          <!-- Summary -->
          <v-card outlined class="mb-4">
            <v-card-text>
              <v-row dense justify="space-between">
                <v-col cols="6" sm="auto">
                  <div class="text-subtitle-2 grey--text mb-1">{{ $t("orders") }}</div>
                  <div class="text-h6 primary--text">{{ orderRowCount }}</div>
                </v-col>
                <v-col cols="6" sm="auto">
                  <div class="text-subtitle-2 grey--text mb-1">{{ $t("spent_on_credits") }}</div>
                  <div class="text-h6 error--text">{{ totalSpent }}</div>
                </v-col>
                <v-col cols="6" sm="auto">
                  <div class="text-subtitle-2 grey--text mb-1">{{ $t("credit_balance") }}</div>
                  <div class="text-h6 success--text">{{ totalBalance }}</div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Filters -->
          <v-row align="center" class="mb-2">
            <v-col cols="auto">
              <month-picker v-model="selectedMonth" color="white" />
            </v-col>
            <v-col cols="3" md="2">
              <text-field v-model="search" kind="search" :label="$t('search')" clearable color="white" />
            </v-col>
            <v-col align="end">
              <v-row no-gutters align="center" justify="end">
                <excel-button class="mr-2" @click="exportToExcel" />
                <print-button @click="printReport" />
              </v-row>
            </v-col>
          </v-row>

          <!-- Table -->
          <v-data-table
            :headers="headers"
            :items="filteredRowsWithTotal"
            hide-default-footer
            disable-pagination
            class="striped-table ordera-box-shadow"
            :loading="loading"
          >
            <template v-slot:item.num="{ item }">
              <span :class="item.isTotal ? 'font-weight-bold' : ''">{{ item.num }}</span>
            </template>

            <template v-slot:item.date="{ item }">
              <span :class="item.isTotal ? 'font-weight-bold' : ''">
                {{ item.isTotal ? $t("total").toUpperCase() : localeDate(item.date) }}
              </span>
            </template>

            <template v-slot:item.restaurant_name="{ item }">
              <span v-if="!item.isTotal">{{ toLatin(item.restaurant_name) }}</span>
            </template>

            <template v-slot:item.meal_name="{ item }">
              <span v-if="!item.isTotal">{{ toLatin(item.meal_name) }}</span>
            </template>

            <template v-slot:item.amount="{ item }">
              <span :class="item.isTotal ? 'font-weight-bold primary--text' : item.amount < 0 ? 'error--text' : ''">
                {{ formatAmount(item.amount, item.currency) }}
              </span>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </template>
  </fullscreen-dialog>
</template>

<script>
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import limitService from "@/services/limit";
import hasModel from "@/mixins/hasModel";
import hasExcel from "@/mixins/hasExcel";
import hasPrinting from "@/mixins/hasPrinting";

export default {
  components: { FullscreenDialog },
  mixins: [hasModel, hasExcel, hasPrinting],
  props: {
    user: { type: Object, default: null },
  },
  data() {
    const now = new Date();
    return {
      selectedMonth: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
      search: "",
      reportData: null,
      loading: false,
    };
  },
  computed: {
    companyId() {
      return this.$store.state.company?.id || null;
    },
    companyRestaurants() {
      return this.$store.state.user.user?.companies?.[0]?.restaurants || [];
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false, width: "80px" },
        { text: this.$t("date"), value: "date", sortable: true },
        { text: this.$t("restaurant"), value: "restaurant_name", sortable: false },
        { text: this.$t("meal"), value: "meal_name", sortable: false },
        { text: this.$t("amount"), value: "amount", sortable: false, align: "right" },
      ];
    },
    monthName() {
      if (!this.selectedMonth) return "";
      return this.localeDate(this.selectedMonth + "-01", { month: "long", year: "numeric" });
    },
    allRows() {
      if (!this.reportData || !this.user) return [];
      const group = (Array.isArray(this.reportData) ? this.reportData : []).find(
        (g) => (g.user?.id ?? null) === this.user.user_id
      );
      const txs = group?.limitTransactions || [];
      return txs
        .sort((a, b) => new Date(a.date || a.created_at) - new Date(b.date || b.created_at))
        .map((t, i) => ({
          id: t.id,
          date: t.date || t.created_at,
          restaurant_id: t.restaurant_id,
          restaurant_name: this.getRestaurantName(t.restaurant_id),
          meal_name: t.orderable?.name || "",
          amount: parseFloat(t.total_amount ?? t.price) || 0,
          currency: this.getCurrencyByRestaurantId(t.restaurant_id),
          isTopup: t.orderable_id === null && t.order_id === null,
          isTotal: false,
          num: i + 1,
        }));
    },
    orderRowCount() {
      return this.filteredRows.filter((r) => r.meal_name).length;
    },
    filteredRows() {
      let rows = this.allRows.filter((r) => !r.isTopup);
      if (this.selectedMonth) {
        const [year, month] = this.selectedMonth.split("-").map(Number);
        rows = rows.filter((r) => {
          if (!r.date) return false;
          const d = new Date(r.date);
          return d.getFullYear() === year && d.getMonth() + 1 === month;
        });
      }
      if (this.search) {
        const q = this.search.toLowerCase();
        rows = rows.filter(
          (r) =>
            (r.meal_name || "").toLowerCase().includes(q) ||
            (r.restaurant_name || "").toLowerCase().includes(q) ||
            this.localeDate(r.date).toLowerCase().includes(q)
        );
      }
      return rows.map((r, i) => ({ ...r, num: i + 1 }));
    },
    filteredRowsWithTotal() {
      const rows = [...this.filteredRows];
      if (rows.length > 0) {
        const currencies = [...new Set(rows.map((r) => r.currency).filter(Boolean))];
        const totalCurrency = currencies.length === 1 ? currencies[0] : "";
        rows.push({
          id: "total",
          num: "",
          date: "",
          restaurant_id: null,
          restaurant_name: "",
          meal_name: "",
          amount: this.filteredTotal,
          currency: totalCurrency,
          isTotal: true,
        });
      }
      return rows;
    },
    filteredTotal() {
      return this.filteredRows.reduce((sum, r) => sum + r.amount, 0);
    },
    totalSpent() {
      const spent = this.allRows
        .filter((r) => r.meal_name)
        .reduce((sum, r) => sum + Math.abs(r.amount), 0);
      return spent.toFixed(2);
    },
    totalBalance() {
      const bal = this.allRows.reduce((sum, r) => sum + r.amount, 0);
      return bal.toFixed(2);
    },
  },
  watch: {
    selectedMonth() {
      this.fetchReport();
    },
    user(val) {
      this.search = "";
      if (val) this.fetchReport();
    },
    model(val) {
      if (val) {
        this.search = "";
        this.fetchReport();
      }
    },
  },
  methods: {
    monthDateRange(yearMonth) {
      const [year, month] = yearMonth.split("-").map(Number);
      const date_from = `${year}-${String(month).padStart(2, "0")}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const date_to = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      return { date_from, date_to };
    },
    getRestaurantName(restaurantId) {
      if (!restaurantId) return "";
      const r = this.companyRestaurants.find((r) => r.id === restaurantId);
      return r?.name || "";
    },
    formatAmount(amount, currency) {
      const cur = currency || "";
      return (parseFloat(amount) || 0).toFixed(2) + (cur ? " " + cur : "");
    },
    async fetchReport() {
      if (!this.user || !this.companyId || !this.selectedMonth) return;
      const { date_from, date_to } = this.monthDateRange(this.selectedMonth);
      this.loading = true;
      this.reportData = null;
      try {
        const { data } = await limitService.getCompanyTransactions(this.companyId, {
          date: date_from,
          date2: date_to,
        });
        this.reportData = data;
      } catch {
        this.reportData = null;
      } finally {
        this.loading = false;
      }
    },
    exportToExcel() {
      const items = this.filteredRows.map((r) => ({
        num: r.num,
        date: this.localeDate(r.date),
        restaurant_name: this.toLatin(r.restaurant_name),
        meal_name: this.toLatin(r.meal_name),
        amount: this.formatAmount(r.amount, r.currency),
      }));
      const currencies = [...new Set(this.filteredRows.map((r) => r.currency).filter(Boolean))];
      const totalCurrency = currencies.length === 1 ? currencies[0] : "";
      items.push({
        num: "",
        date: this.$t("total"),
        restaurant_name: "",
        meal_name: "",
        amount: this.formatAmount(this.filteredTotal, totalCurrency),
      });
      this.downloadExcel({
        fileName: `${this.toLatin(this.user.user_name)}_${this.monthName}.xlsx`,
        sheets: [
          {
            name: this.toLatin(this.user.user_name),
            headers: this.headers,
            items,
            title: this.toLatin(this.user.user_name),
            subtitle: this.monthName,
          },
        ],
      });
    },
    printReport() {
      const items = this.filteredRows.map((r) => ({
        num: r.num,
        date: this.localeDate(r.date),
        restaurant_name: this.toLatin(r.restaurant_name),
        meal_name: this.toLatin(r.meal_name),
        amount: this.formatAmount(r.amount, r.currency),
      }));
      const currencies = [...new Set(this.filteredRows.map((r) => r.currency).filter(Boolean))];
      const totalCurrency = currencies.length === 1 ? currencies[0] : "";
      items.push({
        num: "",
        date: this.$t("total"),
        restaurant_name: "",
        meal_name: "",
        amount: this.formatAmount(this.filteredTotal, totalCurrency),
      });
      this.printDocument({
        title: this.toLatin(this.user.user_name),
        subtitle: this.monthName,
        tables: [{ headers: this.headers, items }],
      });
    },
  },
};
</script>
